// ════════════════════════════════════════════
// GATING UTILITIES — plan & credit gates, teasing helpers
// ════════════════════════════════════════════
import { useAuth } from '../context/AuthContext'

// Hierarchy (higher number = stricter access)
export const PLAN_TIER = {
  free: 0,
  basic: 1,
  createur: 2,
  pro: 3,
}

// Feature → minimum plan requirement (single source of truth for gating).
// Modèle freemium classique : free user a accès à TOUT (pas de cadenas
// visible), seuls les crédits mensuels (1000 cr/mois) limitent l'usage.
// La conversion se déclenche quand les crédits sont épuisés.
export const FEATURE_GATES = {
  // Tier free — gated par crédits mensuels uniquement
  script_generation: 'free',
  merlin_chat: 'free',
  video_analysis: 'free',
  bio_analysis: 'free',
  audience_signals: 'free',
  competitor_bios: 'free',

  // Basic+ features
  format_search: 'basic',
  polish: 'basic',
  evaluate: 'basic',

  // Créateur+ features
  export_scripts: 'createur',
  explore_full: 'createur',
  // Le plan Basic a une analyse profil FIGÉE (snapshot du signup).
  // Créateur+ débloque la mise à jour : re-scan profil, ajout de vidéos,
  // re-analyse vidéo, refresh signaux/strategy/bio.
  profile_refresh: 'createur',

  // Pro-only features
  multi_profile_unlimited: 'pro',
  client_management: 'pro',
  priority_support: 'pro',
}

// Profile quota per plan — seul Pro a les profils illimités, tous les autres plans = 1
export const PROFILE_QUOTAS = {
  free: 1,
  basic: 1,
  createur: 1,
  pro: Infinity,
}

// Check if the effective plan meets the requirement
export function planMeetsRequirement(effectivePlan, requiredPlan) {
  const current = PLAN_TIER[effectivePlan] ?? 0
  const required = PLAN_TIER[requiredPlan] ?? 0
  return current >= required
}

// ════════════════════════════════════════════
// useGate — main hook for feature gating
// Returns { allowed, reason, requiredPlan, currentPlan, planOk, creditOk, remaining, isSimulating }
// ════════════════════════════════════════════
export function useGate(feature, { creditAction = null } = {}) {
  const { plan, canAfford, isUnlimited, remainingCredits, isSimulating } = useAuth()
  const requiredPlan = FEATURE_GATES[feature] || 'free'

  const planOk = planMeetsRequirement(plan, requiredPlan)
  const creditOk = !creditAction || canAfford(creditAction)
  const allowed = isUnlimited || (planOk && creditOk)

  let reason = null
  if (!planOk) reason = 'plan'
  else if (!creditOk) reason = 'credit'

  return {
    allowed,
    reason,
    requiredPlan,
    currentPlan: plan,
    planOk,
    creditOk,
    remaining: remainingCredits(),
    isSimulating,
  }
}

// ════════════════════════════════════════════
// useProfileGate — dedicated hook for profile limit
// ════════════════════════════════════════════
export function useProfileGate(currentProfileCount) {
  const { plan, isUnlimited } = useAuth()
  const quota = PROFILE_QUOTAS[plan] ?? 1
  const canAdd = isUnlimited || currentProfileCount < quota

  let upgradeRequiredPlan = null
  if (!canAdd) {
    if (quota === 1) upgradeRequiredPlan = 'createur'
    else if (quota === 3) upgradeRequiredPlan = 'pro'
  }

  return {
    canAdd,
    quota,
    used: currentProfileCount,
    upgradeRequiredPlan,
  }
}

// ════════════════════════════════════════════
// Plan meta helpers (for UI labels & CTAs)
// ════════════════════════════════════════════
export function planLabel(id) {
  return { free: 'Free', basic: 'Basic', createur: 'Créateur', pro: 'Pro' }[id] || id
}

export function planColor(id) {
  return { free: '#6b7280', basic: '#3b82f6', createur: '#fe8204', pro: '#16a34a' }[id] || '#7c3aed'
}
